// ===========================================
// BUSTED! BLACKJACK - RESTORE AUDIO MODAL
// ===========================================
// Modal shown after page refresh to collect user gesture for browser audio policy.
// Only shown to SPECTATORS - seated players will enable audio via gameplay clicks.
//
// Features:
// - Single [YES] button (no confusing YES/NO choice)
// - 10-second auto-dismiss countdown (non-blocking for passive viewers)
// - Self-resolving: if ignored, modal disappears and user spectates in silence
//
// ELECTRON NOTE: When porting to Electron, this component can be removed entirely.
// Electron allows disabling the autoplay policy via:
//   app.commandLine.appendSwitch('autoplay-policy', 'no-user-gesture-required');
// or per-window:
//   new BrowserWindow({ webPreferences: { autoplayPolicy: 'no-user-gesture-required' } });
//
// Search for "RECONNECT_MODAL" to find all usages to remove for Electron.
// ===========================================

import { useEffect, useState } from 'react';
import { Modal } from '../Modal';

const AUTO_DISMISS_SECONDS = 10;

interface ReconnectModalProps {
  /** Whether to show the modal */
  show: boolean;
  /** Called when user clicks YES to restore audio */
  onRestoreAudio: () => void;
  /** Called when modal auto-dismisses (user didn't click) */
  onDismiss?: () => void;
}

export function ReconnectModal({ show, onRestoreAudio, onDismiss }: ReconnectModalProps) {
  const [countdown, setCountdown] = useState(AUTO_DISMISS_SECONDS);

  // Reset countdown when modal shows
  useEffect(() => {
    if (show) {
      setCountdown(AUTO_DISMISS_SECONDS);
    }
  }, [show]);

  // Countdown timer
  useEffect(() => {
    if (!show) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          // Auto-dismiss
          clearInterval(timer);
          onDismiss?.();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [show, onDismiss]);

  if (!show) return null;

  return (
    <Modal
      variant="default"
      blocking={false}
      showClose={false}
      maxWidthClass="max-w-sm"
    >
      <div className="flex flex-col items-center gap-3 px-8 py-5">
        <span className="font-bold text-2xl tracking-wide text-casino-gold-400">
          RESTORE AUDIO?
        </span>
        <div className="w-full h-1 bg-casino-gold-500 rounded-full" />
        <p className="text-gray-400 text-center">
          Your page was refreshed.<br />
          Click YES to restore audio.
        </p>

        <button
          onClick={onRestoreAudio}
          className="flex items-center justify-center gap-2 px-10 py-3 rounded-lg font-bold text-lg transition-all duration-200 bg-green-500 hover:bg-green-400 text-white shadow-lg hover:shadow-green-500/30 mt-1"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
            <polyline points="20 6 9 17 4 12" />
          </svg>
          YES
        </button>

        {/* Countdown progress bar - CSS animation for smooth shrink from 100% to 0% */}
        <div className="w-full mt-3">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Auto-dismiss</span>
            <span>{countdown}s</span>
          </div>
          <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-casino-gold-500 rounded-full"
              style={{
                animation: `shrinkBar ${AUTO_DISMISS_SECONDS}s linear forwards`,
              }}
            />
          </div>
        </div>

        {/* Keyframes for countdown bar animation */}
        <style>{`
          @keyframes shrinkBar {
            from { width: 100%; }
            to { width: 0%; }
          }
        `}</style>
      </div>
    </Modal>
  );
}

// ===========================================
// LEGACY EXPORT (backwards compatibility)
// ===========================================
// Old props interface for existing usages - maps to new behavior
interface LegacyReconnectModalProps {
  show: boolean;
  onContinue: () => void;
  onLeave: () => void;
}

export function LegacyReconnectModal({ show, onContinue, onLeave }: LegacyReconnectModalProps) {
  return (
    <ReconnectModal
      show={show}
      onRestoreAudio={onContinue}
      onDismiss={onLeave}
    />
  );
}

export default ReconnectModal;
